// "Who is buying?" on the buyer CREATE surfaces (buyer type at creation, owner
// rulings 2026-10-09): the form state, the create-time rule and the request
// fragment, shared by the New buyer page, Quick add and Paste to create. The
// control itself is `BuyerTypeCreateFields`.
//
// Blank is the default and means an individual buyer, exactly as an unanswered
// type does on the record (`BuyerAcquirer.IsCompanyBuyer`). Optional on every
// surface: an individual's create request carries NO buyer-type or company key.

import type { BuyerType, CreateBuyerLeadPayload } from "@/api/buyers";

export interface BuyerTypeCreateState {
  /** "" = not answered (an individual). */
  buyerType: BuyerType | "";
  companyName: string;
  title: string;
  website: string;
}

export const EMPTY_BUYER_TYPE_CREATE: BuyerTypeCreateState = {
  buyerType: "",
  companyName: "",
  title: "",
  website: "",
};

/** `BuyerAcquirer.MaxCompanyNameLength` / `MaxTitleLength` / `MaxWebsiteLength`.
 *  The server refuses a longer value either way; the inputs stop at it. */
export const COMPANY_NAME_MAX_LENGTH = 200;
export const COMPANY_TITLE_MAX_LENGTH = 150;
export const COMPANY_WEBSITE_MAX_LENGTH = 300;

/** A company buyer TYPE — the company rows show for it. */
export function isCompanyTypeChoice(type: BuyerType | "" | null | undefined): boolean {
  return type === "CompanyOwnedOrLed" || type === "InvestmentGroupOrFund";
}

// The server's words (`BuyerAcquirer.CompanyNameRequiredMessage` /
// `CompanyTitleRequiredMessage`), pinned by tests/fixtures/buyer-company-details-cases.json.
export const COMPANY_NAME_REQUIRED_MESSAGE =
  'Enter the acquiring company\'s name, or set "Who is buying?" back to the buyer personally.';
export const COMPANY_TITLE_REQUIRED_MESSAGE =
  'Enter the buyer\'s title at the acquiring company, or set "Who is buying?" back to the buyer personally.';

/**
 * The client mirror of `BuyerAcquirer.CompanyDetailsProblem` (ruling Q1): a
 * company type needs a company name AND a title. Returns the refusal staff
 * create would send, or null. Pinned to the server by the shared fixture
 * `tests/fixtures/buyer-company-details-cases.json`.
 */
export function companyDetailsProblem(
  type: BuyerType | "" | null | undefined,
  companyName: string | null | undefined,
  title: string | null | undefined,
): string | null {
  if (!isCompanyTypeChoice(type)) return null;
  if (!companyName?.trim()) return COMPANY_NAME_REQUIRED_MESSAGE;
  if (!title?.trim()) return COMPANY_TITLE_REQUIRED_MESSAGE;
  return null;
}

/** Which revealed field the problem belongs to, for inline errors. */
export function companyDetailsFieldErrors(state: BuyerTypeCreateState): {
  companyName?: string;
  title?: string;
} {
  const problem = companyDetailsProblem(state.buyerType, state.companyName, state.title);
  if (problem === COMPANY_NAME_REQUIRED_MESSAGE) return { companyName: problem };
  if (problem === COMPANY_TITLE_REQUIRED_MESSAGE) return { title: problem };
  return {};
}

type BuyerTypeFragment = Pick<
  CreateBuyerLeadPayload,
  "buyerType" | "acquiringCompanyName" | "acquiringCompanyTitle" | "acquiringCompanyWebsite"
>;

/**
 * The keys this question adds to a create request.
 *
 * <p>🔴 <b>Hidden is omitted, never sent as "".</b> Blank type → no keys at all
 * (the individual's request is byte-for-byte what it was before this question
 * existed). Individual → the type alone. A company type → the type, name and
 * title, plus the website only when one was typed. Company values typed and
 * then hidden by switching back are dropped, not sent.</p>
 */
export function buyerTypeCreatePayload(state: BuyerTypeCreateState): Partial<BuyerTypeFragment> {
  if (state.buyerType === "") return {};
  if (!isCompanyTypeChoice(state.buyerType)) return { buyerType: state.buyerType };
  const website = state.website.trim();
  return {
    buyerType: state.buyerType,
    acquiringCompanyName: state.companyName.trim(),
    acquiringCompanyTitle: state.title.trim(),
    ...(website ? { acquiringCompanyWebsite: website } : {}),
  };
}
